/* eslint-disable @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-unsafe-selectors  */

/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { type ReactNode, useMemo } from 'react';

import { css, jsx } from '@compiled/react';

import Button from '@atlaskit/button/new';
import CalendarIcon from '@atlaskit/icon/core/calendar';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import { fg } from '@atlaskit/platform-feature-flags';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Inline, Text, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import type { FuzzyDateProps } from '../../types';

import { useDateLabel } from './use-date-label';

const wrapperStyles = css({
	display: 'flex',
	alignItems: 'center',
	width: '100%',
	textAlign: 'left',
});
const normalHeight = css({
	'&& button': {
		paddingBlock: token('space.0'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		lineHeight: '32px',
		height: 'auto',
		textAlign: 'left',
	},
});
const reducedHeight = css({
	'&& button': {
		paddingBlock: token('space.0'),
		height: 'auto',
		textAlign: 'left',
		alignItems: 'center',
	},
});

const dangerStyles = css({
	'&& button': {
		borderColor: token('color.border.danger'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		color: token('color.text.danger'),
	},
	'&& button:hover': {
		backgroundColor: token('color.background.danger.hovered'),
	},
});

const upliftStyles = css({
	display: 'inline-block',
	borderColor: token('color.border'),
	borderWidth: token('border.width'),
	borderStyle: 'solid',
	borderRadius: token('radius.small'),
});

const upliftDangerStyles = css({
	borderColor: token('color.border.danger'),
});

/**
 * These styles replicate the styling of the DatePicker component which doesn't support fuzzy date selection.
 */
const selectBaseStyles = css({
	'&& button': {
		height: token('space.500'),
		alignItems: 'center',
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		backgroundColor: token('color.background.input'),
	},
	'&& button:focus': {
		outline: 'none',
		borderColor: token('color.border.focused'),
		boxShadow: 'inset 0 0 0 1px ' + token('color.border.focused'),
	},
	'&& button::after': {
		display: 'none',
	},
	'&& button:hover': {
		backgroundColor: token('color.background.input.hovered'),
	},
	'&& button:active': {
		backgroundColor: token('color.background.input'),
	},
});

const focusRingVariantStyles = css({
	'&& button': {
		borderColor: token('color.border.focused'),
		boxShadow: 'inset 0 0 0 1px ' + token('color.border.focused'),
		outline: 'none',
	},
});

const normalBorderVariantStyles = css({
	'&& button': {
		borderColor: token('color.border.input'),
	},
});

const nonClickableWrapperStyles = xcss({
	display: 'inline-flex',
	blockSize: 'min-content',
	width: 'max-content',
	height: '32px',
	paddingInlineStart: 'space.075',
	paddingInlineEnd: 'space.150',
	backgroundColor: 'color.background.neutral',
	borderRadius: token('radius.small'),
});

export const DateRange = (props: FuzzyDateProps): JSX.Element => {
	const {
		onClick,
		triggerProps = {},
		placeholder,
		value,
		withTimeScale,
		appearance,
		showIcon = true,
		showPlaceholderWithSubtleText,
		size,
		intervalDisplayName,
		dateLabel: dateLabelOverride,
		selectIconPlacement = 'end',
		forceShowFocusRing = false,
	} = props;

	const computedDateLabel = useDateLabel({
		value,
		placeholder,
		withTimeScale,
		intervalDisplayName,
	});

	const dateLabel = dateLabelOverride ?? computedDateLabel;

	const textColor =
		showPlaceholderWithSubtleText && !value ? 'color.text.subtlest' : 'color.text.subtle';

	const isBundledUpdatesEnabled = fg('goal_types_m3_bundled_updates');
	const isUplift = fg('platform-dst-lozenge-tag-badge-visual-uplifts');
	const isDanger = (isBundledUpdatesEnabled || isUplift) && appearance === 'danger';

	const isSelect = appearance === 'select';
	const { iconBefore, iconAfter } = useMemo(() => {
		if (!isSelect) {
			return {
				iconBefore: () => (
					<CalendarIcon
						label="calendar icon"
						color={isDanger ? token('color.icon.danger') : undefined}
					/>
				),
				iconAfter: showIcon ? () => <ChevronDownIcon label="" size="small" /> : undefined,
			};
		}

		if (selectIconPlacement === 'start') {
			return {
				iconBefore: () => (
					<CalendarIcon label="" color={isDanger ? token('color.icon.danger') : undefined} />
				),
			};
		}

		if (!showIcon || selectIconPlacement !== 'end') {
			return { iconBefore: undefined };
		}

		return {
			iconBefore: undefined,
			iconAfter: () => <CalendarIcon label="" />,
		};
	}, [isSelect, selectIconPlacement, showIcon, isDanger]);

	if (!onClick) {
		return (
			<Inline
				alignBlock="center"
				space="space.025"
				xcss={nonClickableWrapperStyles}
				testId="date-range-trigger-wrapper"
			>
				<CalendarIcon color="currentColor" spacing="spacious" label="calendar icon" />
				<Text weight="medium" color={textColor}>
					{dateLabel}
				</Text>
			</Inline>
		);
	}

	const showReducedHeight = size !== 'medium';

	let buttonContent: ReactNode;
	if (isSelect) {
		buttonContent = (
			<Text weight={'regular'} color={textColor}>
				{dateLabel}
			</Text>
		);
	} else if (isUplift) {
		buttonContent = (
			<Text weight="medium" color={isDanger ? 'color.text.danger' : textColor}>
				{dateLabel}
			</Text>
		);
	} else if (showPlaceholderWithSubtleText) {
		buttonContent = <Text color={isDanger ? 'color.text.danger' : textColor}>{dateLabel}</Text>;
	} else {
		buttonContent = dateLabel;
	}

	return (
		<div
			css={[
				wrapperStyles,
				showReducedHeight ? reducedHeight : normalHeight,
				isSelect && selectBaseStyles,
				isSelect && forceShowFocusRing ? focusRingVariantStyles : normalBorderVariantStyles,
				!isUplift && isDanger && dangerStyles,
				isUplift && upliftStyles,
				isUplift && isDanger && upliftDangerStyles,
			]}
			data-testid="date-range-trigger-wrapper"
		>
			<Button
				{...triggerProps}
				shouldFitContainer
				onClick={onClick}
				iconBefore={iconBefore}
				iconAfter={iconAfter}
				testId="date-range-trigger"
				{...(isUplift && { spacing: 'compact', appearance: 'subtle' })}
			>
				{buttonContent}
			</Button>
		</div>
	);
};
